import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Hotel, Car, Map, Plane, Camera, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import zelij3 from "../assets/zelij5.jpg";
import { t } from "../contexts/translations";
import AirbnbDatePicker from "./ui/AirbnbDatePicker";

/* Date du jour au format ISO (local) pour borner les calendriers. */
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/* Titre + phrase d'aide par onglet (style Airbnb/Booking) : on dit clairement
   quoi faire, sans encadré marketing superflu. */
const TAB_COPY = {
  sejour: {
    title: "Trouvez votre hébergement idéal",
    subtitle: "Sélectionnez vos dates d'arrivée et de départ, puis lancez votre réservation.",
  },
  circuit: {
    title: "Partez à l'aventure",
    subtitle: "Choisissez votre date de départ pour explorer nos circuits authentiques.",
  },
  transport: {
    title: "Voyagez en toute sérénité",
    subtitle: "Indiquez la date de votre trajet pour organiser votre transport.",
  },
  location: {
    title: "Prenez la route librement",
    subtitle: "Choisissez vos dates de retrait et de retour pour votre voiture.",
  },
  visite: {
    title: "Explorez la culture marocaine",
    subtitle: "Sélectionnez la date de votre visite guidée accompagnée.",
  },
};

/* ---------------------------------------------
   Tabs
---------------------------------------------- */
const getTabs = (tFn, language) => [
  {
    key: "sejour",
    label: tFn("tabSejour", language),
    shortLabel: tFn("tabSejourShort", language),
    icon: <Hotel className="w-5 h-5" />,
  },
  {
    key: "circuit",
    label: tFn("tabCircuit", language),
    shortLabel: tFn("tabCircuitShort", language),
    icon: <Map className="w-5 h-5" />,
  },
  {
    key: "transport",
    label: tFn("tabTransport", language),
    shortLabel: tFn("tabTransportShort", language),
    icon: <Plane className="w-5 h-5" />,
  },
  {
    key: "location",
    label: tFn("tabLocation", language),
    shortLabel: tFn("tabLocationShort", language),
    icon: <Car className="w-5 h-5" />,
  },
  {
    key: "visite",
    label: tFn("tabVisite", language),
    shortLabel: tFn("tabVisiteShort", language),
    icon: <Camera className="w-5 h-5" />,
  },
];

/* ---------------------------------------------
   HeroSection (sans filtres)
---------------------------------------------- */
const HeroSection = ({ language }) => {
  const [activeTab, setActiveTab] = useState("sejour");
  const navigate = useNavigate();
  const TABS = useMemo(() => getTabs(t, language), [language]);

  // Dates sélectionnées par onglet. sejour/location = plage ; les autres = date simple.
  const [dates, setDates] = useState({
    sejour: { start: "", end: "" },
    location: { start: "", end: "" },
    circuit: "",
    transport: "",
    visite: "",
  });
  const setTabDates = (key, val) => setDates((p) => ({ ...p, [key]: val }));

  // Hide scrollbar for tabs (webkit)
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .hero-tabs::-webkit-scrollbar { display: none; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // ✅ Redirection par tab, en passant les dates choisies via l'URL.
  // Chaque page cible lit ces paramètres pour pré-remplir + lancer la recherche.
  const goToForm = (tabKey) => {
    const qs = (obj) => {
      const p = new URLSearchParams();
      Object.entries(obj).forEach(([k, v]) => v && p.set(k, v));
      const s = p.toString();
      return s ? `?${s}` : "";
    };

    switch (tabKey) {
      case "sejour":
        navigate(`/sejour${qs({ dateArrivee: dates.sejour.start, dateDepart: dates.sejour.end })}`);
        break;
      case "circuit":
        navigate(`/circuit${qs({ date: dates.circuit })}`);
        break;
      case "transport":
        navigate(`/transport${qs({ dateDepart: dates.transport })}`);
        break;
      case "location":
        navigate(`/location${qs({ pickupDate: dates.location.start, returnDate: dates.location.end })}`);
        break;
      case "visite":
        navigate(`/guided-tours${qs({ date: dates.visite })}`);
        break;
      default:
        navigate("/");
    }
  };

  // Le sélecteur de dates adapté à l'onglet courant.
  const renderDatePicker = (tabKey) => {
    if (tabKey === "sejour" || tabKey === "location") {
      const isStay = tabKey === "sejour";
      return (
        <AirbnbDatePicker
          inline
          mode="range"
          language={language}
          min={todayISO()}
          value={dates[tabKey]}
          onChange={(v) => setTabDates(tabKey, v)}
          startLabel={isStay ? t("stayCheckIn", language) : t("carPickupDate", language)}
          endLabel={isStay ? t("stayCheckOut", language) : t("carReturnDate", language)}
        />
      );
    }
    const singleLabelKey =
      tabKey === "circuit" ? "circuitTravelDate" : tabKey === "transport" ? "transportDate" : "visitDate";
    return (
      <AirbnbDatePicker
        inline
        mode="single"
        language={language}
        min={todayISO()}
        value={dates[tabKey]}
        onChange={(v) => setTabDates(tabKey, v)}
        singleLabel={t(singleLabelKey, language)}
      />
    );
  };

  // Texte bouton par tab
  const reserveLabel = (tabKey) => {
    switch (tabKey) {
      case "sejour":
        return t("reserveStay", language);
      case "circuit":
        return t("reserveCircuit", language);
      case "transport":
        return t("reserveTransport", language);
      case "location":
        return t("reserveCar", language);
      case "visite":
        return t("reserveTour", language);
      default:
        return t("reserve", language);
    }
  };

  // Icône bouton par tab
  const reserveIcon = (tabKey) => {
    switch (tabKey) {
      case "sejour":
        return <Hotel className="w-5 h-5" />;
      case "circuit":
        return <Map className="w-5 h-5" />;
      case "transport":
        return <Plane className="w-5 h-5" />;
      case "location":
        return <Car className="w-5 h-5" />;
      case "visite":
        return <Camera className="w-5 h-5" />;
      default:
        return null;
    }
  };

  // Background images for tabs
  const tabBackgrounds = {
    sejour: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80",
    circuit: "https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=1200&q=80",
    transport: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=1200&q=80",
    location: "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=1200&q=80",
    visite: "https://images.unsplash.com/photo-1548013146-72479768bbaa?w=1200&q=80"
  };

  return (
    <section
      className="relative min-h-screen flex items-center justify-center shadow-xl rounded-b-3xl"
      style={{
        backgroundImage: `url(${zelij3}), linear-gradient(135deg, #f5b675 30%, #d97706 100%)`,
        backgroundBlendMode: "overlay",
        backgroundSize: "400px, cover",
        backgroundRepeat: "repeat, no-repeat",
        backgroundPosition: "center center, center center",
      }}
    >
      <div className="absolute inset-0 bg-black/40" />

      <div className="relative z-10 w-full flex flex-col items-center justify-center px-3 sm:px-4 md:px-6 lg:px-8 py-8 sm:py-12 md:py-20 pt-20 sm:pt-24 md:pt-20">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-3xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mt-2 sm:mt-4 md:mt-12 mb-4 sm:mb-6 leading-tight drop-shadow-2xl text-center"
        >
          {t("heroTitle", language)
            .split("\n")
            .map((line, index) => (
              <React.Fragment key={index}>
                <span
                  className={`block ${
                    index === 0
                      ? "text-white"
                      : "text-amber-200 text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium italic mt-2"
                  }`}
                >
                  {line}
                </span>
              </React.Fragment>
            ))}
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-base sm:text-lg md:text-2xl mb-6 sm:mb-8 md:mb-10 opacity-90 max-w-3xl mx-auto drop-shadow-lg text-center text-amber-50 px-2"
        >
          {t("heroSubtitle", language)}
        </motion.p>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-[2.5rem] p-0 text-gray-800 max-w-5xl w-full border border-orange-200/50 mx-auto overflow-hidden relative"
        >
          {/* Dynamic Background Image for the whole box */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 0.35, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="absolute inset-0 z-0 pointer-events-none"
              style={{
                backgroundImage: `url(${tabBackgrounds[activeTab]})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </AnimatePresence>
          {/* Tabs Container */}
          <div
            className="hero-tabs relative z-10 flex w-full overflow-x-auto justify-start md:justify-center bg-white/40 backdrop-blur-md border-b border-gray-100"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div className="flex w-full md:w-auto p-2 gap-1">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    className={`relative flex-1 md:flex-none min-w-[80px] sm:min-w-[120px] flex flex-col sm:flex-row items-center justify-center gap-2 px-4 py-3 sm:py-4 rounded-2xl text-xs sm:text-sm font-bold transition-all duration-300 group`}
                    onClick={() => setActiveTab(tab.key)}
                    type="button"
                  >
                    {/* Background Glider */}
                    {isActive && (
                      <motion.div
                        layoutId="navTab"
                        className="absolute inset-0 bg-white shadow-md rounded-2xl z-0 border border-orange-100"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}

                    <span className={`relative z-10 transition-colors duration-300 ${isActive ? 'text-orange-600' : 'text-gray-500 group-hover:text-gray-700'}`}>
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 5 }}
                        whileTap={{ scale: 0.9 }}
                        animate={isActive ? { scale: 1.1 } : { scale: 1 }}
                      >
                        {tab.icon}
                      </motion.div>
                    </span>
                    
                    <span className={`relative z-10 transition-colors duration-300 hidden sm:inline truncate ${isActive ? 'text-orange-600' : 'text-gray-500 group-hover:text-gray-700'}`}>
                      {tab.label}
                    </span>
                    <span className={`relative z-10 transition-colors duration-300 sm:hidden text-[10px] truncate leading-tight ${isActive ? 'text-orange-600' : 'text-gray-500 group-hover:text-gray-700'}`}>
                      {tab.shortLabel}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="relative z-10 p-6 sm:p-10 md:p-12 backdrop-blur-sm bg-white/40">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center justify-center gap-6"
              >
                {/* En-tête épuré : titre + phrase d'aide claire (style Booking) */}
                <div className="text-center max-w-xl px-2">
                  <h3 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                    {TAB_COPY[activeTab]?.title}
                  </h3>
                  <p className="mt-2 text-gray-600 text-sm sm:text-base font-medium">
                    {TAB_COPY[activeTab]?.subtitle}
                  </p>
                </div>

                {/* Sélecteur de dates (style Airbnb) — adapté à l'onglet */}
                <div className="w-full max-w-md relative z-20">
                  <p className="text-xs font-bold uppercase tracking-wider text-orange-600/80 mb-2 text-center">
                    {activeTab === "sejour" || activeTab === "location"
                      ? t("heroPickDates", language)
                      : t("heroPickDate", language)}
                  </p>
                  {renderDatePicker(activeTab)}
                </div>

                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 40px -10px rgba(249, 115, 22, 0.4)" }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={() => goToForm(activeTab)}
                  className="w-full sm:w-auto bg-gradient-to-r from-orange-500 via-orange-600 to-red-600 text-white px-12 py-5 rounded-[1.5rem] text-lg font-black uppercase tracking-widest shadow-2xl transition-all flex items-center justify-center gap-4 group"
                >
                  <span className="transition-transform group-hover:rotate-12 duration-300">
                    {reserveIcon(activeTab)}
                  </span>
                  {reserveLabel(activeTab)}
                  <ChevronRight className="w-6 h-6 transition-transform group-hover:translate-x-2" />
                </motion.button>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
